import React, { useState, useEffect, useRef } from 'react'

interface StatCardProps {
  value: string;
  label: string;
  sublabel?: string;
  icon?: string;
  imageUrl?: string;
  delay?: number;
  color?: string; 
}

const StatCard: React.FC<StatCardProps> = ({
  value,
  label,
  sublabel,
  icon,
  imageUrl,
  delay = 0,
  color = 'rg-green' 
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Create a new IntersectionObserver to detect when this card is visible
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)
          observer.unobserve(entries[0].target)
        }
      },
      { threshold: 0.2 }
    )

    // Start observing current card
    if (cardRef.current) {
      observer.observe(cardRef.current)
    }

    // Cleanup
    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current)
      }
    }
  }, [])

  // Get the actual color value based on the limited palette (yellow, rg-green, green)
  const getColorValue = (colorName: string) => {
    const colorMap: Record<string, string> = {
      'yellow': '#E5B423',
      'rg-green': '#A9C23F',
      'green': '#3FC266'
    }
    
    return colorMap[colorName] || colorMap['rg-green']
  }

  return (
    <div 
      className="stat-card" 
      style={{ animationDelay: `${delay}ms` }}
      ref={cardRef}
      data-color={color} 
    >
      <div className="stat-content">
        <h3 className="stat-value" style={{ color: getColorValue(color) }}>
          {isVisible ? value : ''}
        </h3>
        <p className="stat-label">{label}</p>
        {sublabel && <p className="stat-sublabel">{sublabel}</p>}
      </div>
      
      {imageUrl && (
        <div className="stat-image">
          <img src={imageUrl} alt={label} />
        </div>
      )}
      
      {!imageUrl && icon && (
        <div className="stat-image">
          <i className={`fas fa-${icon} fa-4x`} style={{ color: getColorValue(color) }}></i>
        </div>
      )}
    </div>
  )
}

export default StatCard